import { useMemo } from 'react'
import {
  getStorage, ref, uploadBytes, getDownloadURL, deleteObject, listAll,
} from 'firebase/storage'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { documentNameProblem, type DocumentsOperations } from '@wehunt/ui'
import {
  createTeamDocument,
  batchUpdateTeamDocuments,
  batchDeleteTeamDocuments,
  downloadTeamDocument,
  moveTeamDocument,
  type TeamDocumentDto,
} from './api/teamDocumentsApi'

export const MAX_FILE_BYTES = 100 * 1024 * 1024 // 100 MB

// ─── Pure helpers ────────────────────────────────────────────────────────────

export function buildFullPath(groupId: string, relPath: string): string {
  return relPath ? `documents/${groupId}/${relPath}` : `documents/${groupId}`
}

/**
 * The canonical (Unicode NFC) spelling of a document or folder name, applied
 * before the name becomes part of a Storage object path.
 *
 * An accented name has two spellings: composed, where "Å" is a single
 * codepoint, and decomposed, where it is a plain "A" followed by a combining
 * ring. They render identically but are different bytes, and a Storage object
 * name is matched as bytes.
 *
 * A file picked in a browser on macOS (or in the app on iOS) reports the
 * decomposed spelling, so the object used to be stored under a decomposed
 * name — and such an object is unreachable through its own download URL: the
 * URL is built from the decomposed path, but the request that finally reaches
 * Storage carries the composed one, addressing a name that does not exist.
 * Storage answers that with 403 rather than 404, because a download token
 * authorizes only the exact object it belongs to. Hence the symptom: a
 * document with å, ä or ö in its name cannot be opened, while an ASCII-named
 * document in the same folder opens fine.
 *
 * Composing as the name enters keeps every stored name in the one spelling
 * that every client and every URL layer agrees on, and it is what makes the
 * duplicate-name checks meaningful: the two spellings are unequal as strings,
 * so without this an upload could land next to a file whose name looks
 * exactly the same.
 */
export function canonicalDocumentName(name: string): string {
  return name.normalize('NFC')
}

/**
 * Refuses a name that cannot be one Storage path segment. The documents dialog
 * already rejects these with a localized message; this is the backstop for any
 * caller that reaches the write path without it, because what it prevents is
 * silent — a "/" in a name creates nested folders instead of failing.
 */
function assertValidDocumentName(name: string): void {
  if (documentNameProblem(name) !== null) {
    throw new Error(`"${name}" can’t be used as a name.`)
  }
}

export function resolveUniqueName(name: string, existing: Set<string>): string {
  if (!existing.has(name)) return name
  const dot = name.lastIndexOf('.')
  const base = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  for (let i = 1; ; i++) {
    const candidate = `${base}(${i})${ext}`
    if (!existing.has(candidate)) return candidate
  }
}

// ─── Internal helpers ────────────────────────────────────────────────────────

async function listExistingNames(groupId: string, parentRelPath: string): Promise<Set<string>> {
  const storage = getStorage()
  const result = await listAll(ref(storage, buildFullPath(groupId, parentRelPath)))
  // Composed on the way in, so a name check compares like with like: objects
  // written before names were composed carry the decomposed spelling. Used for
  // comparison only — no Storage path is built out of this set.
  const names = new Set<string>()
  result.items.forEach((item) => {
    const n = item.fullPath.split('/').at(-1)!
    if (n !== '.keep') names.add(canonicalDocumentName(n))
  })
  result.prefixes.forEach((p) =>
    names.add(canonicalDocumentName(p.fullPath.split('/').at(-1)!)),
  )
  return names
}

async function storageDelete(path: string): Promise<void> {
  const storage = getStorage()
  const r = ref(storage, path)
  const { items, prefixes } = await listAll(r)
  if (items.length === 0 && prefixes.length === 0) { await deleteObject(r); return }
  await Promise.all(items.map((i) => deleteObject(i)))
  await Promise.all(prefixes.map((p) => storageDelete(p.fullPath)))
}

/**
 * The metadata rows to re-point once the objects at `srcFull` have moved to
 * `destFull`: the item's own row and, for a folder, every row below it.
 */
function repointedRows(
  currentDocs: TeamDocumentDto[],
  srcFull: string,
  destFull: string,
): Array<{ id: string; fileName: string; storagePath: string }> {
  return currentDocs
    .filter(({ storagePath: sp }) => sp === srcFull || sp.startsWith(`${srcFull}/`))
    .map((d) => {
      const newSp = destFull + d.storagePath.slice(srcFull.length)
      return { id: d.id, fileName: newSp.split('/').at(-1)!, storagePath: newSp }
    })
}

// ─── Exported operations ──────────────────────────────────────────────────────

export async function execCreateFolder(
  groupId: string,
  parentRelPath: string,
  name: string,
  userId: string,
  userName: string | null,
  token: string,
): Promise<void> {
  assertValidDocumentName(name)
  const folderName = canonicalDocumentName(name)
  const folderRelPath = parentRelPath ? `${parentRelPath}/${folderName}` : folderName
  const storage = getStorage()
  await uploadBytes(
    ref(storage, `${buildFullPath(groupId, folderRelPath)}/.keep`),
    new Uint8Array(0),
  )
  await createTeamDocument(
    groupId,
    {
      fileName: folderName,
      storagePath: buildFullPath(groupId, folderRelPath),
      isFolder: true,
      uploaderId: userId || null,
      uploaderName: userName || null,
    },
    token,
  )
}

/**
 * Explicit UTF-8 Content-Type for text uploads. The OS-provided `file.type`
 * for a .txt/.csv is a charset-less `text/plain`/`text/csv`, which the browser
 * then decodes with its locale default (windows-1252 in Western Europe), so
 * åäö open as mojibake (CLUB-551). Returns null for non-text files, which keep
 * their own inferred type.
 */
function textCharsetContentType(fileName: string): string | null {
  const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase()
  if (ext === 'txt') return 'text/plain; charset=utf-8'
  if (ext === 'csv') return 'text/csv; charset=utf-8'
  return null
}

export async function execUploadFiles(
  groupId: string,
  parentRelPath: string,
  files: FileList,
  userId: string,
  userName: string | null,
  token: string,
): Promise<void> {
  const existing = await listExistingNames(groupId, parentRelPath)
  const storage = getStorage()
  for (const file of Array.from(files)) {
    if (file.size > MAX_FILE_BYTES) {
      throw new Error(`"${file.name}" exceeds the 100 MB limit.`)
    }
    const finalName = resolveUniqueName(canonicalDocumentName(file.name), existing)
    existing.add(finalName)
    const relPath = parentRelPath ? `${parentRelPath}/${finalName}` : finalName
    const storagePath = buildFullPath(groupId, relPath)
    const contentType = textCharsetContentType(file.name)
    await uploadBytes(
      ref(storage, storagePath),
      file,
      contentType ? { contentType } : undefined,
    )
    await createTeamDocument(
      groupId,
      {
        fileName: finalName,
        storagePath,
        isFolder: false,
        uploaderId: userId || null,
        uploaderName: userName || null,
      },
      token,
    )
  }
}

export async function execGetFileUrl(
  groupId: string,
  relPath: string,
): Promise<string> {
  return getDownloadURL(ref(getStorage(), buildFullPath(groupId, relPath)))
}

/**
 * The file's bytes, fetched through the API rather than from Firebase Storage.
 *
 * The Storage SDK's `getBlob` reads the bucket's `?alt=media` endpoint over XHR,
 * and that endpoint answers with the bucket's own CORS policy — which names no
 * web origin — so the browser blocked every one of those reads before it left
 * the machine. Firebase serves listing, metadata and delete itself with
 * permissive CORS, which is why only the byte reads failed, and why the failure
 * showed up as "spreadsheets cannot be opened": an image or a PDF is rendered
 * from a tokenized download URL in an <img>/<iframe>, which is not a
 * CORS-governed request, while a spreadsheet has no preview and the download is
 * the whole interaction.
 *
 * `/api` is same-origin (nginx proxies it), so these bytes arrive with no CORS
 * negotiation at all and can be saved under the file's own name.
 */
export async function execGetFileBlob(
  groupId: string,
  relPath: string,
  token: string,
): Promise<Blob> {
  return downloadTeamDocument(groupId, relPath, token)
}

export async function execDeleteItems(
  groupId: string,
  items: Array<{ relPath: string; isFolder: boolean }>,
  token: string,
  currentDocs: TeamDocumentDto[],
): Promise<void> {
  await Promise.all(items.map((item) => storageDelete(buildFullPath(groupId, item.relPath))))
  const idsToDelete = currentDocs
    .filter(({ storagePath: sp }) =>
      items.some((item) => {
        const full = buildFullPath(groupId, item.relPath)
        return sp === full || sp.startsWith(`${full}/`)
      }),
    )
    .map((d) => d.id)
  if (idsToDelete.length > 0) {
    await batchDeleteTeamDocuments(groupId, idsToDelete, token)
  }
}

/**
 * Moves each item into `destFolderRelPath`, then re-points its metadata rows.
 *
 * The objects are moved by the API, never from here: a move copies an object,
 * a copy reads its bytes, and the bucket's CORS policy blocks every byte read
 * the browser makes — so every move and rename used to fail on web, and a
 * folder's empty `.keep` marker (which skipped the read) moved without its files.
 */
export async function execMoveItems(
  groupId: string,
  items: Array<{ relPath: string; isFolder: boolean }>,
  destFolderRelPath: string,
  token: string,
  currentDocs: TeamDocumentDto[],
): Promise<void> {
  const updates: Array<{ id: string; fileName: string; storagePath: string }> = []
  for (const item of items) {
    const name = item.relPath.split('/').at(-1)!
    const destRelPath = destFolderRelPath ? `${destFolderRelPath}/${name}` : name
    await moveTeamDocument(groupId, item.relPath, destRelPath, item.isFolder, token)
    updates.push(
      ...repointedRows(
        currentDocs,
        buildFullPath(groupId, item.relPath),
        buildFullPath(groupId, destRelPath),
      ),
    )
  }
  if (updates.length > 0) {
    await batchUpdateTeamDocuments(groupId, updates, token)
  }
}

/** A rename is a move within the same folder, done by the API like any other. */
export async function execRenameItem(
  groupId: string,
  relPath: string,
  newName: string,
  isFolder: boolean,
  token: string,
  currentDocs: TeamDocumentDto[],
): Promise<void> {
  assertValidDocumentName(newName)
  const segs = relPath.split('/')
  segs[segs.length - 1] = canonicalDocumentName(newName)
  const destRelPath = segs.join('/')
  await moveTeamDocument(groupId, relPath, destRelPath, isFolder, token)
  const updates = repointedRows(
    currentDocs,
    buildFullPath(groupId, relPath),
    buildFullPath(groupId, destRelPath),
  )
  if (updates.length > 0) {
    await batchUpdateTeamDocuments(groupId, updates, token)
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDocumentOperations(
  groupId: string,
  refresh: () => void,
): DocumentsOperations {
  const { state } = useAuth()
  const queryClient = useQueryClient()
  const userId = state.status === 'signed-in' ? state.user.uid : ''
  const userName =
    state.status === 'signed-in' ? (state.user.displayName ?? null) : null

  return useMemo(
    (): DocumentsOperations => {
      const getToken = async (): Promise<string> => {
        if (state.status !== 'signed-in') {
          throw new Error('Cannot perform document operations while signed out.')
        }
        return state.user.getIdToken()
      }

      const getCurrentDocs = (): TeamDocumentDto[] =>
        queryClient.getQueryData<TeamDocumentDto[]>(['team-documents', groupId]) ?? []

      const invalidate = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['team-documents', groupId] })
      }

      return {
        // Read-only, so no cache invalidation: a view-only member reaches these
        // two, and neither one changes the listing. getFileBlob still needs a
        // token — it goes through the API, which gates on team membership, and
        // membership alone is what it requires: reading a team's documents is
        // open to every member, including a view-only one.
        getFileUrl: (relPath) => execGetFileUrl(groupId, relPath),
        getFileBlob: async (relPath) =>
          execGetFileBlob(groupId, relPath, await getToken()),
        createFolder: async (name, parentRelPath) => {
          const token = await getToken()
          await execCreateFolder(groupId, parentRelPath, name, userId, userName, token)
          await invalidate()
          refresh()
        },
        uploadFiles: async (files, parentRelPath) => {
          const token = await getToken()
          await execUploadFiles(groupId, parentRelPath, files, userId, userName, token)
          await invalidate()
          refresh()
        },
        deleteItems: async (items) => {
          const token = await getToken()
          await execDeleteItems(groupId, items, token, getCurrentDocs())
          await invalidate()
          refresh()
        },
        moveItems: async (items, destFolderRelPath) => {
          const token = await getToken()
          await execMoveItems(groupId, items, destFolderRelPath, token, getCurrentDocs())
          await invalidate()
          refresh()
        },
        renameItem: async (relPath, newName, isFolder) => {
          const token = await getToken()
          await execRenameItem(groupId, relPath, newName, isFolder, token, getCurrentDocs())
          await invalidate()
          refresh()
        },
      }
    },
    [groupId, userId, userName, refresh, queryClient, state],
  )
}
